/* What the host has switched on, asked once per launch.
 *
 * Four things live here and none of them is a secret: whether there is a
 * PostHog project and where it is, whether there is a Sentry project, whether
 * sign-up is invitation only, and the partner links. The service reads all
 * four from its own environment and answers `GET /v1/config` with no auth, so
 * this app bundles no key and a host that has set nothing gets a build that
 * sends nothing.
 *
 * **Null means off, everywhere.** A missing key, an empty string, a body this
 * cannot make sense of and a request that never landed all end up in the same
 * place: the feature is off and the screen says nothing about it. There is no
 * default project and no fallback host, because a tracker that turns itself on
 * when the service is unreachable is the one failure mode nobody would find.
 *
 * **The last answer is remembered, for the copy rather than for the tracking.**
 * `signup` decides what the sign-in screen says, and a screen that renders the
 * open sentence for 300ms and then swaps it for the invitation one has told a
 * parent two different things about the same product. So the last answer is
 * written down and quoted on the next launch while the fresh one is in flight,
 * which is what `entitlement.ts` does with the cost sentence and for the same
 * reason. Telemetry is started from the fresh answer only.
 *
 * **Unknown is not "open" and not "invite".** Until an answer lands, `useConfig`
 * reports null and every screen shows the copy that claims nothing.
 */
import { useEffect, useSyncExternalStore } from "react";
import { config as fetchConfig } from "./api";
import type { Config, PartnerLink } from "./types";

const KEY = "mh.config";

/* -------------------------------------------------------------- live hosts
 *
 * A partner link goes to the live site or it does not render.
 *
 * The rule is written as a property of the address rather than as a list of
 * the two partners, because the list is the operator's to change from the
 * service's environment and a client that held its own copy would quietly
 * refuse a third partner. What it refuses is a scheme that is not https, an
 * address with no host, and any host carrying one of the labels a staging box
 * wears. `edustrategy.me` passes; `dev.edustrategy.me`, `qa.uniplan.me` and
 * anything on localhost do not.
 */
const NOT_LIVE = new Set([
  "dev", "qa", "staging", "stage", "test", "uat", "preview", "sandbox",
  "local", "localhost",
]);

export function isLiveHost(href: string): boolean {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }
  if (url.protocol !== "https:") return false;
  const host = url.hostname.toLowerCase();
  if (host.length === 0) return false;
  /* An address, not a name. Nothing the operator publishes is an IP and a
     staging box very often is one. */
  if (/^[0-9.]+$/.test(host) || host.includes(":")) return false;
  const labels = host.split(".");
  if (labels.length < 2) return false;
  return !labels.some((label) => NOT_LIVE.has(label));
}

/** The partner links that may be rendered, in the order the service sent them.
 *  Filtering here rather than in the strip, so no screen in the app can render
 *  a link this rule refuses. */
export function livePartners(partners: PartnerLink[]): PartnerLink[] {
  return partners.filter((p) => p.name.length > 0 && isLiveHost(p.href));
}

/* --------------------------------------------------------------- the store */

const listeners = new Set<() => void>();

let held: Config | null = null;
let read = false;

function fromStorage(): Config | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const body = JSON.parse(raw) as Config;
    /* The same shape check the client makes of a fresh body. A remembered
       config is a body like any other and is trusted no further. */
    return shaped(body);
  } catch {
    // Safari private browsing throws on read. No config, and every screen
    // shows the copy that claims nothing.
    return null;
  }
}

/** Whatever a body is, in the shape this app will act on, or null.
 *
 *  Exported because the spec asserts on it directly: the rule that an empty
 *  key is the same as no key is worth a test that does not have to drive a
 *  browser to reach it. */
export function shaped(body: unknown): Config | null {
  if (typeof body !== "object" || body === null) return null;
  const raw = body as Record<string, unknown>;

  const ph = raw.posthog as Record<string, unknown> | null | undefined;
  const key = typeof ph?.key === "string" ? ph.key.trim() : "";
  const host = typeof ph?.host === "string" ? ph.host.trim() : "";
  const posthog = key.length > 0 && host.length > 0 ? { key, host } : null;

  const sn = raw.sentry as Record<string, unknown> | null | undefined;
  const dsn = typeof sn?.dsn === "string" ? sn.dsn.trim() : "";
  const sentry = dsn.length > 0 ? { dsn } : null;

  /* Anything but the two words the contract names is treated as the open
     service, because the open copy claims nothing that could be wrong. */
  const signup = raw.signup === "invite" ? "invite" : "open";

  const partner = Array.isArray(raw.partner)
    ? livePartners((raw.partner as unknown[])
      .filter((p): p is PartnerLink =>
        typeof (p as PartnerLink)?.name === "string"
        && typeof (p as PartnerLink)?.href === "string"))
    : [];

  return { posthog, sentry, signup, partner };
}

/** The last answer this device heard, or null on a device that has heard
 *  none. */
export function last(): Config | null {
  if (!read) {
    held = fromStorage();
    read = true;
  }
  return held;
}

function remember(cfg: Config): void {
  held = cfg;
  read = true;
  try {
    localStorage.setItem(KEY, JSON.stringify(cfg));
  } catch {
    /* nothing to do about it: the next launch asks again */
  }
  for (const fn of listeners) fn();
}

let asking: Promise<Config | null> | null = null;

/** Ask the service, once per launch however many screens want the answer.
 *
 *  Answers null where the service said nothing this app can use, and the
 *  remembered answer is left alone in that case: a request that did not land
 *  is not a host turning its telemetry off. */
export function refresh(): Promise<Config | null> {
  if (!asking) {
    asking = fetchConfig().then((body) => {
      const cfg = shaped(body);
      if (cfg) remember(cfg);
      return cfg;
    });
  }
  return asking;
}

/** For a test that needs a second launch in one process. */
export function forget(): void {
  asking = null;
  held = null;
  read = false;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to do about it */
  }
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** What the host has switched on, or null while nobody has been told yet. */
export function useConfig(): Config | null {
  const cfg = useSyncExternalStore(subscribe, last, () => null);
  useEffect(() => {
    void refresh();
  }, []);
  return cfg;
}
